import { z } from 'zod';

export const RangeSchema = z.union([z.tuple([z.number(), z.number()]), z.number()]);
export const RangeSetSchema = z.array(RangeSchema);
export type RangeEntry = z.infer<typeof RangeSchema>;
export type RangeSet = z.infer<typeof RangeSetSchema>;

const MAX_RANGE = 10000;

// Parse single range part (e.g., "1-5" or "10")
const parseRangePart = (rangePart: string): RangeEntry | null => {
  if (rangePart.includes('-')) {
    const [start, end] = rangePart.split('-').map((numStr) => parseInt(numStr.trim(), 10));
    return !isNaN(start) && !isNaN(end) && start <= end ? [start, end] : null;
  }

  const number = parseInt(rangePart, 10);
  return !isNaN(number) ? number : null;
};

// Expand single entry to array of numbers
const expandEntry = (entry: RangeEntry): number[] =>
  Array.isArray(entry)
    ? Array.from({ length: entry[1] - entry[0] + 1 }, (_unused, index) => entry[0] + index)
    : [entry];

// Expand all entries to flat sorted unique array
const expandAndSort = (ranges: RangeSet): number[] =>
  Array.from(new Set(ranges.flatMap(expandEntry))).sort((a, b) => a - b);

// Collapse consecutive numbers into ranges
const collapseToRanges = (sortedNumbers: number[]): RangeSet => {
  if (sortedNumbers.length === 0) return [];

  const ranges: RangeSet = [];
  let rangeStart = sortedNumbers[0];
  let rangeEnd = sortedNumbers[0];

  for (let index = 1; index < sortedNumbers.length; index++) {
    const current = sortedNumbers[index];

    if (current === rangeEnd + 1) {
      rangeEnd = current;
    } else {
      ranges.push(rangeEnd === rangeStart ? rangeStart : [rangeStart, rangeEnd]);
      rangeStart = current;
      rangeEnd = current;
    }
  }

  ranges.push(rangeEnd === rangeStart ? rangeStart : [rangeStart, rangeEnd]);
  return ranges;
};

// Validate single number is within bounds
const isValidNumber = (num: number, max = MAX_RANGE): boolean => num >= 1 && num <= max;

// Validate range entry
const isValidEntry = (entry: RangeEntry | null, max = MAX_RANGE): boolean => {
  if (entry === null) return false;
  if (Array.isArray(entry)) {
    return isValidNumber(entry[0], max) && isValidNumber(entry[1], max);
  }
  return isValidNumber(entry, max);
};

// Parse string to compact range array
const parse = (input: string): RangeSet => {
  const parts = input
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.map(parseRangePart).filter((entry): entry is RangeEntry => entry !== null);
};

// Convert range array to string
const toString = (ranges: RangeSet | undefined): string => {
  if (!ranges) {
    return '';
  }

  return ranges
    .map((entry) => (Array.isArray(entry) ? `${entry[0]}-${entry[1]}` : `${entry}-${entry}`))
    .join(',');
};

// Normalize: sort, dedupe, merge consecutive ranges
const normalize = (input: RangeSet | string): RangeSet => {
  const ranges = typeof input === 'string' ? parse(input) : input;
  return collapseToRanges(expandAndSort(ranges));
};

// Count total numbers in range set
const count = (ranges: RangeSet): number =>
  ranges.reduce(
    (totalCount: number, entry: RangeEntry) =>
      totalCount + (Array.isArray(entry) ? entry[1] - entry[0] + 1 : 1),
    0,
  );

// Validate range string
const isValid = (input: RangeSet | string, max = MAX_RANGE): boolean => {
  if ((typeof input === 'string' && !input.trim()) || !input.length) return true;

  const parts = typeof input === 'string' ? input.split(',').map((part) => part.trim()) : input;
  return parts.every(
    (part) => part && isValidEntry(typeof part === 'string' ? parseRangePart(part) : part, max),
  );
};

// Check if range is empty
const isEmpty = (input: RangeSet | string | undefined | null): boolean => {
  if (input === null || input === undefined) return true;
  if (typeof input === 'string') return !input.trim();
  return input.length === 0;
};

export const Ranges = {
  parse,
  toString,
  normalize,
  count,
  isValid,
  isEmpty,
};
